import { useEffect, useState } from 'react';
import { ReactP5Wrapper } from '@p5-wrapper/react';
import { calculateDrawSize, displaySketch, editSketch, setParameters, getParameters, setCanvasDimensions } from '../libs/BitmapEditor/bitmapMaker3';
import styled from 'styled-components';
import { useForceUpdate } from '../hooks/forceUpdate';
import Box from '@mui/material/Box';
import { simulateMouseClick } from '../helpers/click';
import MinusIcon from '../assets/minus.png';
import PlusIcon from '../assets/plus.png';
import FullScreenIcon from '../assets/fullscreen.png';
import CustomButton from './Button';
import Tools from './Tools';
import { getCreateWith } from '../helpers/createWith';
import { CircularProgress, Typography } from '@mui/material';
import CanvasSizeModal from './CanvasSizeModal';
import BackgroundImageAdjustmentModal from './BackgroundImageAdjustmentModal';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';

declare global {
	interface Window {
		editSketch: any;
		displaySketch: any;
	}
}

function Canvas() {
	const handleForceupdateMethod = useForceUpdate();
	const p5 = () => window.editSketch; // helper to get the p5 instance of editSketch
	const createWith = getCreateWith();
	const [isLoading, setIsLoading] = useState(false);
	const [canvasSize, setCanvasSize] = useState(310); // Default fallback size
	const [canvasDimensionsSet, setCanvasDimensionsSet] = useState(false);
	const [showSizeModal, setShowSizeModal] = useState(true);
	const [showBackgroundAdjustmentModal, setShowBackgroundAdjustmentModal] = useState(false);
	const [backgroundImageData, setBackgroundImageData] = useState<{
		image: string;
		grayscale: number;
		opacity: number;
	} | null>(null);

	// Color history context
	const { addColorToHistory } = useColorHistoryContext();

	// Check if we have previous bitmap data on mount
	useEffect(() => {
		const checkForExistingBitmap = async () => {
			if (createWith && createWith.get) {
				try {
					const previousBitmap = await createWith.get();
					if (previousBitmap?.image?.image) {
						// If we have a previous bitmap, we can extract dimensions and skip the modal
						// For now, let's assume it's the default size and skip modal
						// In the future, you might want to extract actual dimensions from the image
						handleCanvasDimensionsConfirm(100, 100);
						return;
					}
				} catch (error) {
					console.error('Error checking for existing bitmap:', error);
				}
			}
		};

		checkForExistingBitmap();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Calculate optimal canvas size based on screen dimensions (portrait only)
	const calculateCanvasSize = () => {
		const screenWidth = window.innerWidth;
		const screenHeight = window.innerHeight;

		// Use 95% of screen width
		const maxWidthFromScreen = screenWidth * 0.95;

		// Calculate available height (screen height minus space for buttons and tools)
		// Estimate: buttons (~60px) + tools (~100px) + margins (~40px) = ~200px
		const reservedHeight = 200;
		const maxHeightFromScreen = screenHeight - reservedHeight;

		// Take the smaller of the two to ensure everything fits
		const optimalSize = Math.min(maxWidthFromScreen, maxHeightFromScreen);

		// Ensure minimum size of 280px and maximum of 500px for usability
		const clampedSize = Math.max(280, Math.min(600, optimalSize));

		return Math.floor(clampedSize);
	};

	// Handle canvas dimension selection
	const handleCanvasDimensionsConfirm = async (width: number, height: number) => {
		// Update parameters with user's chosen dimensions
		const currentParams = getParameters();
		currentParams.width = width;
		currentParams.height = height;
		currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
		setParameters(currentParams);

		// Calculate responsive canvas size
		const newSize = calculateCanvasSize();
		setCanvasSize(newSize);
		setCanvasDimensions(newSize, newSize);

		// Hide size modal
		setShowSizeModal(false);

		// Check for background image and show adjustment modal if available
		if (createWith && createWith.get_background_image) {
			try {
				const backgroundImage = await createWith.get_background_image();

				if (backgroundImage?.image?.image) {
					const imageData = {
						image: backgroundImage.image.image,
						grayscale: backgroundImage.image.grayscale ?? 0, // Default to 0 if not provided
						opacity: backgroundImage.image.opacity ?? 1, // Default to 1 if not provided
					};
					setBackgroundImageData(imageData);
					setShowBackgroundAdjustmentModal(true);
					return; // Don't set canvasDimensionsSet yet
				}
			} catch (error) {
				console.error('Error checking for background image:', error);
			}
		}

		// If no background image, proceed directly
		setCanvasDimensionsSet(true);
	};

	// Handle background image adjustment confirmation
	const handleBackgroundAdjustmentConfirm = (grayscale: number, opacity: number) => {
		if (backgroundImageData) {
			// Update background image data with new adjustments
			const updatedData = {
				...backgroundImageData,
				grayscale,
				opacity,
			};
			setBackgroundImageData(updatedData);

			// Apply the background image with adjustments to parameters
			const currentParams = getParameters();
			currentParams.background_image = updatedData.image;
			currentParams.background_grayscale = grayscale; // Already in 0-100 range
			currentParams.background_opacity = opacity; // Already in 0-1 range
			setParameters(currentParams);
		}

		// Hide adjustment modal and proceed to canvas
		setShowBackgroundAdjustmentModal(false);
		setCanvasDimensionsSet(true);
	};

	// Handle skipping background adjustments
	const handleBackgroundAdjustmentSkip = () => {
		if (backgroundImageData) {
			// Apply the background image without adjustments
			const currentParams = getParameters();
			currentParams.background_image = backgroundImageData.image;
			currentParams.background_grayscale = backgroundImageData.grayscale; // Already in 0-100 range
			currentParams.background_opacity = backgroundImageData.opacity; // Already in 0-1 range
			setParameters(currentParams);
		}

		// Hide adjustment modal and proceed to canvas
		setShowBackgroundAdjustmentModal(false);
		setCanvasDimensionsSet(true);
	};

	// Set canvas size once on mount (only if dimensions are already set)
	useEffect(() => {
		if (canvasDimensionsSet) {
			const newSize = calculateCanvasSize();
			setCanvasSize(newSize);
			setCanvasDimensions(newSize, newSize);
		}
	}, [canvasDimensionsSet]);

	const initialLoad = async () => {
		if (createWith && (createWith.get || createWith.get_background_image)) {
			try {
				setIsLoading(true);
				// Request the previous bitmap from the mobile app
				const previousBitmap = await createWith.get();
				const backgroundImage = await createWith.get_background_image();

				if (previousBitmap?.image?.image) {
					// Set the previous bitmap as background image (scale kept, so cropped)
					const currentParams = getParameters();
					currentParams.snapshot = previousBitmap.image.image;
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
				} else if (backgroundImage?.image?.image) {
					// Set the background image (scaled down to canvas size)
					const currentParams = getParameters();
					currentParams.background_image = backgroundImage.image.image;
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
					// Also update the display sketch background image
					if (window?.displaySketch?.updateBackgroundImage) {
						window.displaySketch.updateBackgroundImage(backgroundImage.image.image);
					}
				} else {
					// No previous bitmap or background image, just set the callback
					const currentParams = getParameters();
					currentParams.onColorUsed = addColorToHistory; // Set the color usage callback
					setParameters(currentParams);
				}
			} catch (error) {
				console.error('Error loading previous bitmap:', error);
			} finally {
				setIsLoading(false);
			}
		} else {
			// Set the callback even if no createWith methods are available
			const currentParams = getParameters();
			currentParams.onColorUsed = addColorToHistory;
			setParameters(currentParams);
		}
		// add async promise to give canvas time to render properly
		await new Promise(resolve => setTimeout(resolve, 300));
		if (p5()) {
			setFullScreenCanvas();
			// simulate touch for mobile webView
			simulateMouseClick(document);
		}
	};

	useEffect(() => {
		if (canvasDimensionsSet) {
			initialLoad();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasDimensionsSet]);

	const updateZoom = (zoom: number) => {
		p5()?.updateZoom(zoom);
		handleForceupdateMethod();
	};

	const setFullScreenCanvas = () => {
		if (p5()) {
			const currentParams = getParameters();
			const drawSize = calculateDrawSize(currentParams.width, currentParams.height); // Use dynamic dimensions
			p5().setDrawSize(drawSize ?? 1);

			// Update the view center coordinates to the new center
			p5().viewCenterX = p5().canvasCentreX;
			p5().viewCenterY = p5().canvasCentreY;
			// Recalculate the screen adjustment for the new view center
			p5().updateScreenAdjust(); // You can call this function directly
			// Force re-render after the update
			setTimeout(() => {
				handleForceupdateMethod();
			}, 300);
		}
	};

	// Show loading spinner
	if (isLoading) {
		return <CircularProgress color="primary" />;
	}

	// Show canvas size modal first
	if (showSizeModal) {
		return <CanvasSizeModal open={showSizeModal} onConfirm={handleCanvasDimensionsConfirm} />;
	}

	// Show background adjustment modal if background image is available
	if (showBackgroundAdjustmentModal && backgroundImageData) {
		return (
			<BackgroundImageAdjustmentModal
				open={showBackgroundAdjustmentModal}
				backgroundImage={backgroundImageData.image}
				initialGrayscale={backgroundImageData.grayscale} // Already in 0-100 range from mobile app
				initialOpacity={backgroundImageData.opacity * 100} // Convert from 0-1 to 0-100 range
				onConfirm={handleBackgroundAdjustmentConfirm}
				onSkip={handleBackgroundAdjustmentSkip}
			/>
		);
	}

	// Show main canvas interface only after dimensions are set
	if (!canvasDimensionsSet) {
		return <CircularProgress color="primary" />;
	}

	return (
		<Screen canvasSize={canvasSize}>
			<Buttons canvasSize={canvasSize}>
				<Box display="flex" alignItems="center" gap={1}>
					<CustomButton onClick={() => updateZoom(-0.5)} image={MinusIcon} label="Zoom Out" imageSize={40} />
					<Typography mr={1} fontSize={12} fontWeight={700}>
						{p5()?.getZoomPercentage() || 100}%
					</Typography>
					<CustomButton onClick={() => updateZoom(0.5)} image={PlusIcon} label="Zoom In" imageSize={40} />
				</Box>
				<CustomButton onClick={() => setFullScreenCanvas()} image={FullScreenIcon} label="Full Canvas" imageSize={40} imagePadding={4} />
			</Buttons>

			<Box display="none">
				<ReactP5Wrapper sketch={displaySketch} />
			</Box>
			<EditCanvas canvasSize={canvasSize}>
				<ReactP5Wrapper sketch={editSketch} />
			</EditCanvas>

			<Tools />
		</Screen>
	);
}

export default Canvas;

const Screen = styled.div.withConfig({
	shouldForwardProp: prop => prop !== 'canvasSize',
})<{ canvasSize: number }>`
	margin: 0 auto;
	width: ${props => props.canvasSize}px;
	max-width: 95vw;
	max-height: 100vh;
`;

const EditCanvas = styled.div.withConfig({
	shouldForwardProp: prop => prop !== 'canvasSize',
})<{ canvasSize: number }>`
	margin: 15px 0;
	width: ${props => props.canvasSize}px;
	height: ${props => props.canvasSize}px;
	max-width: ${props => props.canvasSize}px;
	max-height: ${props => props.canvasSize}px;
	border-radius: 15px;
	overflow: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: none;
	touch-action: none;
`;

const Buttons = styled.div.withConfig({
	shouldForwardProp: prop => prop !== 'canvasSize',
})<{ canvasSize: number }>`
	display: flex;
	flew-direction: row;
	align-items: center;
	justify-content: space-between;
	width: ${props => props.canvasSize}px;
	max-width: 95vw;
	padding: 0 15px;
	margin-top: 15px;
`;
